import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { 
  UtensilsCrossed, Loader2, Calendar, AlertTriangle, 
  Check, Plus, Trash2, Settings, GripVertical, Printer, Sun, Moon, Coffee, Copy, CalendarDays, X
} from 'lucide-react';
import { toast } from 'sonner';
import { getPrintToolbarCSS, getPrintToolbarHTML } from '../utils/printHelper';

const MEALS = [
  { code: 'colazione', name: 'Colazione', icon: Coffee, color: 'amber' },
  { code: 'pranzo', name: 'Pranzo', icon: Sun, color: 'orange' },
  { code: 'cena', name: 'Cena', icon: Moon, color: 'indigo' }
];

const DAYS = [
  { code: 'lunedi', name: 'Lunedì' },
  { code: 'martedi', name: 'Martedì' },
  { code: 'mercoledi', name: 'Mercoledì' },
  { code: 'giovedi', name: 'Giovedì' },
  { code: 'venerdi', name: 'Venerdì' },
  { code: 'sabato', name: 'Sabato' },
  { code: 'domenica', name: 'Domenica' }
];

// Helper: Extract only numbers from allergen codes (1a -> 1, 7b -> 7)
const getAllergenNumbers = (allergens) => {
  if (!allergens || allergens.length === 0) return '';
  return allergens.map(a => a.replace(/[a-z]/gi, '')).join(',');
};

// Allergens are now loaded from database - see fetchAllergens()

const getWeekNumber = (date) => {
  // ISO week number calculation
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Set to Thursday of current week
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const getWeekDates = (weekNumber, year = new Date().getFullYear()) => {
  // Find the first Monday of week 1 (ISO standard)
  const jan4 = new Date(year, 0, 4); // Jan 4 is always in week 1
  const dayOfWeek = jan4.getDay() || 7; // Sunday = 7
  const firstMonday = new Date(jan4);
  firstMonday.setDate(jan4.getDate() - dayOfWeek + 1);
  
  // Calculate the Monday of the requested week
  const targetMonday = new Date(firstMonday);
  targetMonday.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
  
  // Generate dates for Mon-Sun
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(targetMonday);
    date.setDate(targetMonday.getDate() + i);
    dates.push({
      day: date.getDate(),
      month: date.getMonth() + 1,
      formatted: `${date.getDate()}/${date.getMonth() + 1}`
    });
  }
  return dates;
};

// Allergen Selector Component - receives allergens list from parent
const AllergenSelector = ({ selected = [], onChange, disabled, allergensList = [] }) => {
  const toggleAllergen = (code) => {
    if (selected.includes(code)) {
      onChange(selected.filter(a => a !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  // Generate allergens with codes for display (1a, 1b for traces, etc.)
  const displayAllergens = [];
  allergensList.forEach(allergen => {
    // Main allergen (e.g., 1a)
    displayAllergens.push({
      code: `${allergen.number}a`,
      name: allergen.name,
      dbCode: allergen.code
    });
    // Traces version (e.g., 1b)
    displayAllergens.push({
      code: `${allergen.number}b`,
      name: `${allergen.name} (tracce)`,
      dbCode: `${allergen.code}_tracce`
    });
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs" disabled={disabled}>
          <AlertTriangle className="w-3 h-3 mr-1" />
          {selected.length > 0 ? `${selected.length}` : 'Allergeni'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-96 overflow-y-auto" align="start">
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700 mb-3">Seleziona allergeni</p>
          <div className="grid grid-cols-2 gap-2">
            {displayAllergens.map(allergen => (
              <div key={allergen.code} className="flex items-center space-x-2">
                <Checkbox
                  id={allergen.code}
                  checked={selected.includes(allergen.code)}
                  onCheckedChange={() => toggleAllergen(allergen.code)}
                />
                <label htmlFor={allergen.code} className="text-xs cursor-pointer">
                  <span className="font-mono font-bold mr-1">{allergen.code}</span>
                  {allergen.name.replace(' (tracce)', '')}
                </label>
              </div>
            ))}
          </div>
          {displayAllergens.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">Caricamento allergeni...</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Autocomplete Input - shows suggestions while typing
const AutocompleteInput = ({
  value = '',
  onChange,
  onSelect,
  options = [],
  placeholder = '',
  disabled = false,
  className = '',
  getAllergenNumbers,
  groupedOptions = null // { 'Carboidrato': [...], 'Verdura': [...] }
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Get all options (flat or grouped)
  const allOptions = groupedOptions 
    ? Object.values(groupedOptions).flat() 
    : options;
  
  // Filter suggestions based on input
  const suggestions = value.trim().length >= 1
    ? allOptions.filter(opt => 
        opt.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8) // Max 8 suggestions
    : [];
  
  // Group suggestions if we have grouped options
  const groupedSuggestions = groupedOptions && suggestions.length > 0
    ? Object.entries(groupedOptions).reduce((acc, [group, opts]) => {
        const filtered = opts.filter(opt => 
          opt.name.toLowerCase().includes(value.toLowerCase())
        );
        if (filtered.length > 0) acc[group] = filtered;
        return acc;
      }, {})
    : null;
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelect = (option) => {
    onSelect(option);
    setShowSuggestions(false);
  };
  
  const shouldShow = focused && showSuggestions && suggestions.length > 0 && value.trim().length >= 1;
  
  return (
    <div ref={containerRef} className="relative flex-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => {
          setFocused(true);
          if (value.trim().length >= 1) setShowSuggestions(true);
        }}
        onBlur={() => {
          setFocused(false);
          // Delay hiding to allow click on suggestion
          setTimeout(() => setShowSuggestions(false), 150);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      
      {/* Suggestions dropdown */}
      {shouldShow && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {groupedSuggestions ? (
            // Grouped suggestions (for contorni)
            Object.entries(groupedSuggestions).map(([group, opts]) => {
              const isCarb = group.toLowerCase().includes('carboidrato');
              const icon = isCarb ? '🥔' : '🥗';
              const bgClass = isCarb ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
              
              return (
                <div key={group}>
                  <p className={`text-xs font-semibold px-2 py-1 ${bgClass} sticky top-0`}>
                    {icon} {group}
                  </p>
                  {opts.slice(0, 4).map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(opt);
                      }}
                    >
                      <span className="truncate">{opt.name}</span>
                      {opt.allergens?.length > 0 && (
                        <span className="text-[10px] text-amber-600 font-mono ml-1">
                          ({getAllergenNumbers(opt.allergens)})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              );
            })
          ) : (
            // Regular suggestions
            suggestions.map(opt => (
              <button
                key={opt.id}
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                <span className="truncate">{opt.name}</span>
                {opt.allergens?.length > 0 && (
                  <span className="text-[10px] text-amber-600 font-mono ml-1">
                    ({getAllergenNumbers(opt.allergens)})
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Dish Selector Component with search - compact dialog for selecting dishes
const DishSelector = ({ 
  options = [], 
  currentValue = '', 
  onSelect, 
  placeholder = 'Cerca piatto...',
  title = 'Seleziona piatto',
  getAllergenNumbers,
  showLastUsed = true,
  triggerClassName = '',
  groupedOptions = null // { 'Carboidrato': [...], 'Verdura': [...] } for contorni
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  
  // Filter options by search
  const filterOptions = (opts) => {
    if (!search.trim()) return opts;
    return opts.filter(o => 
      o.name.toLowerCase().includes(search.toLowerCase())
    );
  };
  
  // Get last used display
  const getLastUsedInfo = (option) => {
    if (!showLastUsed || !option.last_used) {
      return { text: 'mai usato ✓', className: 'text-green-600 font-medium' };
    }
    
    const lastUsed = new Date(option.last_used);
    const today = new Date();
    const diffDays = Math.floor((today - lastUsed) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return { text: 'usato oggi', className: 'text-red-500 font-medium' };
    if (diffDays === 1) return { text: 'usato ieri', className: 'text-red-500' };
    if (diffDays <= 3) return { text: `${diffDays}gg fa`, className: 'text-amber-500' };
    if (diffDays <= 7) return { text: `${diffDays}gg fa`, className: 'text-blue-500' };
    return { text: `${diffDays}gg fa ✓`, className: 'text-green-600 font-medium' };
  };
  
  const renderOption = (option) => {
    const lastUsed = getLastUsedInfo(option);
    const isSelected = currentValue === option.name;
    
    return (
      <button
        key={option.id}
        type="button"
        className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 transition-colors ${
          isSelected ? 'bg-blue-50 text-blue-700' : ''
        }`}
        onClick={() => {
          onSelect(option);
          setOpen(false);
          setSearch('');
        }}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium truncate">{option.name}</span>
          {option.allergens?.length > 0 && (
            <span className="text-[10px] text-amber-600 font-mono ml-1 flex-shrink-0">
              ({getAllergenNumbers(option.allergens)})
            </span>
          )}
        </div>
        {showLastUsed && (
          <p className={`text-[10px] ${lastUsed.className}`}>
            ⏱ {lastUsed.text}
          </p>
        )}
      </button>
    );
  };

  // Calculate if we have grouped options (for contorni)
  const hasGroups = groupedOptions && Object.keys(groupedOptions).length > 0;
  const allOptions = hasGroups 
    ? Object.values(groupedOptions).flat() 
    : options;
  const filteredAll = filterOptions(allOptions);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-7 w-7 p-0 hover:bg-blue-100 ${triggerClassName}`}
          title={title}
        >
          <Plus className="w-3.5 h-3.5 text-blue-600" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-64 p-0" 
        align="end"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b">
          <Input
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {filteredAll.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-3">
              {search ? 'Nessun risultato' : 'Nessuna opzione disponibile'}
            </p>
          ) : hasGroups ? (
            // Grouped display (for contorni)
            Object.entries(groupedOptions).map(([groupName, groupOptions]) => {
              const filtered = filterOptions(groupOptions);
              if (filtered.length === 0) return null;
              
              const isCarb = groupName.toLowerCase().includes('carboidrato');
              const icon = isCarb ? '🥔' : '🥗';
              const bgClass = isCarb ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
              
              return (
                <div key={groupName}>
                  <p className={`text-xs font-semibold px-2 py-1 rounded ${bgClass} sticky top-0`}>
                    {icon} {groupName}
                  </p>
                  {filtered.map(renderOption)}
                </div>
              );
            })
          ) : (
            // Regular list
            filteredAll.map(renderOption)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const MenuConfigPage = () => {
  const { api, isAdmin, isKitchen, isSuperAdmin, activeStructure } = useAuth();
  const [categories, setCategories] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [weekNumber, setWeekNumber] = useState(getWeekNumber(new Date()));
  
  // Get current day of week (0=Sunday, 1=Monday, etc.) and map to our day codes
  const getDayCode = () => {
    const dayIndex = new Date().getDay();
    const dayCodes = ['domenica', 'lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato'];
    return dayCodes[dayIndex];
  };
  const [activeDay, setActiveDay] = useState(getDayCode());
  const [activeMeal, setActiveMeal] = useState('pranzo'); // colazione, pranzo, cena
  const [structureName, setStructureName] = useState('');
  
  // Calculate week dates based on week number
  const weekDates = getWeekDates(weekNumber);
  
  // Auto-save state
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'pending'
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});
  
  // Category management
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [newCategoryMealType, setNewCategoryMealType] = useState('pranzo_cena');
  const [newCategoryHasSideDish, setNewCategoryHasSideDish] = useState(false);
  const [allCategories, setAllCategories] = useState([]); // All categories for management
  
  // Delete category confirmation
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);
  
  // Category options management
  const [categoriesWithOptions, setCategoriesWithOptions] = useState([]);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [selectedCategoryForOptions, setSelectedCategoryForOptions] = useState(null);
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionAllergens, setNewOptionAllergens] = useState([]);
  const [editingOption, setEditingOption] = useState(null);
  
  // Apply menu dialog (for breakfast)
  const [applyMenuDialogOpen, setApplyMenuDialogOpen] = useState(false);
  const [applyMode, setApplyMode] = useState('week'); // 'week' or 'until'
  const [applyUntilDate, setApplyUntilDate] = useState(null);
  
  // Selected options for quick selection (pranzo/cena)
  const [selectedDayOptions, setSelectedDayOptions] = useState({}); // {categoryId: optionId}

  // Who can manage categories: admin, kitchen, super admin
  const canManageCategories = isAdmin || isKitchen || isSuperAdmin;
  const canEditMenu = isAdmin || isKitchen || isSuperAdmin; // operatore is read-only

  const fetchCategories = useCallback(async () => {
    try {
      // Fetch categories filtered by current meal type
      const mealTypeParam = activeMeal === 'colazione' ? 'colazione' : 'pranzo_cena';
      const response = await api.get('/categories', { params: { meal_type: mealTypeParam } });
      setCategories(response.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [api, activeMeal]);

  const fetchAllCategories = useCallback(async () => {
    try {
      // Fetch ALL categories for management dialog
      const response = await api.get('/categories');
      setAllCategories(response.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Failed to fetch all categories:', error);
    }
  }, [api]);

  const fetchAllergens = useCallback(async () => {
    try {
      const response = await api.get('/allergies');
      setAllergens(response.data.sort((a, b) => a.number - b.number));
    } catch (error) {
      console.error('Failed to fetch allergens:', error);
    }
  }, [api]);

  const fetchCategoriesWithOptions = useCallback(async () => {
    try {
      const mealTypeParam = activeMeal === 'colazione' ? 'colazione' : 'pranzo_cena';
      const response = await api.get('/menu/options', { params: { meal_type: mealTypeParam } });
      setCategoriesWithOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch categories with options:', error);
    }
  }, [api, activeMeal]);

  const fetchMenu = useCallback(async () => {
    const currentYear = new Date().getFullYear();
    try {
      const response = await api.get('/menu', { params: { week_number: weekNumber, year: currentYear, meal_type: activeMeal } });
      const grouped = {};
      response.data.forEach(item => {
        if (!grouped[item.day]) grouped[item.day] = {};
        grouped[item.day][item.category_code] = {
          dish_name: item.dish_name,
          allergens: item.allergens || []
        };
      });
      setMenuItems(grouped);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    } finally {
      setLoading(false);
    }
  }, [api, weekNumber, activeMeal]);

  // Fetch structure name
  const fetchStructureName = useCallback(async () => {
    try {
      // First try activeStructure (for Super Admin)
      if (activeStructure?.name) {
        setStructureName(activeStructure.name);
        return;
      }
      
      const meRes = await api.get('/auth/me');
      if (meRes.data.structure_id) {
        const structRes = await api.get(`/structures/${meRes.data.structure_id}`);
        setStructureName(structRes.data.name);
      }
    } catch (error) {
      // Fallback
      if (activeStructure?.name) {
        setStructureName(activeStructure.name);
      }
    }
  }, [api, activeStructure]);

  useEffect(() => { fetchCategories(); fetchAllergens(); fetchCategoriesWithOptions(); }, [fetchCategories, fetchAllergens, fetchCategoriesWithOptions, activeMeal]);
  useEffect(() => { setLoading(true); fetchMenu(); }, [fetchMenu, weekNumber, activeMeal]);
  useEffect(() => { fetchStructureName(); }, [fetchStructureName]);
  useEffect(() => { if (categoryDialogOpen) fetchAllCategories(); }, [categoryDialogOpen, fetchAllCategories]);

  // Auto-save function
  const saveMenuItem = useCallback(async (day, categoryCode, data) => {
    if (!data.dish_name?.trim() && !data.side_dish?.trim()) return;
    
    const currentYear = new Date().getFullYear();
    
    try {
      await api.post('/menu', {
        week_number: weekNumber,
        year: currentYear,
        day,
        meal_type: activeMeal,
        category_code: categoryCode,
        dish_name: data.dish_name?.trim() || '',
        allergens: data.allergens || [],
        side_dish: data.side_dish?.trim() || null,
        side_dish_allergens: data.side_dish_allergens || []
      });
    } catch (error) {
      console.error('Failed to save menu item:', error);
      throw error;
    }
  }, [api, weekNumber, activeMeal]);

  const processPendingChanges = useCallback(async () => {
    const changes = { ...pendingChangesRef.current };
    pendingChangesRef.current = {};
    
    if (Object.keys(changes).length === 0) {
      setSaveStatus('saved');
      return;
    }

    setSaveStatus('saving');
    
    try {
      const promises = Object.entries(changes).map(([key, data]) => {
        const [day, categoryCode] = key.split('|');
        return saveMenuItem(day, categoryCode, data);
      });
      
      await Promise.all(promises);
      setSaveStatus('saved');
    } catch (error) {
      setSaveStatus('pending');
      toast.error('Errore nel salvataggio');
    }
  }, [saveMenuItem]);

  const handleDishChange = (day, categoryCode, field, value) => {
    // Update local state immediately
    setMenuItems(prev => {
      const dayData = prev[day] || {};
      const catData = dayData[categoryCode] || { dish_name: '', allergens: [], side_dish: '', side_dish_allergens: [] };
      
      const newData = {
        ...prev,
        [day]: {
          ...dayData,
          [categoryCode]: {
            ...catData,
            [field]: value
          }
        }
      };
      
      // Queue for auto-save
      const key = `${day}|${categoryCode}`;
      pendingChangesRef.current[key] = newData[day][categoryCode];
      
      return newData;
    });
    
    // Set pending status and schedule save
    setSaveStatus('pending');
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      processPendingChanges();
    }, 1500); // Save after 1.5 seconds of inactivity
  };

  // Category management functions
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Nome categoria obbligatorio');
      return;
    }
    
    const code = newCategoryCode.trim() || newCategoryName.toLowerCase().replace(/\s+/g, '_');
    
    try {
      await api.post('/categories', {
        name: newCategoryName.trim(),
        code,
        order: categories.length + 1,
        meal_type: newCategoryMealType,
        has_side_dish: newCategoryHasSideDish
      });
      toast.success('Categoria aggiunta');
      setNewCategoryName('');
      setNewCategoryCode('');
      setNewCategoryMealType('pranzo_cena');
      setNewCategoryHasSideDish(false);
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Open delete category dialog
  const openDeleteCategoryDialog = (category) => {
    setCategoryToDelete(category);
    setDeleteCategoryDialogOpen(true);
  };

  // Confirm delete category
  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    setDeleteCategoryLoading(true);
    try {
      await api.delete(`/categories/${categoryToDelete.id}`);
      toast.success('Categoria eliminata');
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  const handleDeleteCategory = (category) => {
    openDeleteCategoryDialog(category);
  };

  // Options management functions
  const openOptionsDialog = (category) => {
    setSelectedCategoryForOptions(category);
    setNewOptionName('');
    setNewOptionAllergens([]);
    setEditingOption(null);
    setOptionDialogOpen(true);
  };

  // Parse dish entry with optional allergens: "Pasta al pomodoro 1,7" or "Insalata verde"
  const parseDishEntry = (entry) => {
    const trimmed = entry.trim();
    if (!trimmed) return null;
    
    // Regex: nome piatto seguito opzionalmente da numeri separati da virgola
    // Match: "Pasta al pomodoro 1,7" -> name="Pasta al pomodoro", allergenNums=["1","7"]
    // Match: "Insalata verde" -> name="Insalata verde", allergenNums=[]
    const match = trimmed.match(/^(.+?)\s+([\d,\s]+)$/);
    
    if (match) {
      const name = match[1].trim();
      const allergenNums = match[2].split(',').map(n => n.trim()).filter(n => n.length > 0);
      return { name, allergenNums };
    } else {
      // No allergen numbers, just the dish name
      return { name: trimmed, allergenNums: [] };
    }
  };

  // Convert allergen numbers to codes (e.g., 1 -> "1a")
  const convertAllergenNumbersToCodes = (allergenNums) => {
    const codes = [];
    const invalidNums = [];
    
    for (const numStr of allergenNums) {
      const num = parseInt(numStr, 10);
      if (isNaN(num)) continue;
      
      // Find allergen in the list by number
      const allergen = allergens.find(a => a.number === num);
      if (allergen) {
        codes.push(`${num}a`); // "a" suffix means allergen is present (not traces)
      } else {
        invalidNums.push(numStr);
      }
    }
    
    return { codes, invalidNums };
  };

  const handleAddOption = async () => {
    if (!newOptionName.trim() || !selectedCategoryForOptions) return;
    
    // NEW SYNTAX: Piatti separati da punto e virgola (;)
    // Allergeni dopo il nome, separati da virgola
    // Esempio: "Pasta al pomodoro 1,7; Risotto ai funghi 1; Insalata verde"
    
    const inputText = newOptionName.trim();
    
    // Check if using new syntax (contains ;) or old syntax (just comma-separated names)
    const usesNewSyntax = inputText.includes(';');
    
    let dishesToAdd = [];
    
    if (usesNewSyntax) {
      // NEW SYNTAX: split by semicolon
      const entries = inputText.split(';').map(e => e.trim()).filter(e => e.length > 0);
      
      for (const entry of entries) {
        const parsed = parseDishEntry(entry);
        if (parsed) {
          const { codes, invalidNums } = convertAllergenNumbersToCodes(parsed.allergenNums);
          
          if (invalidNums.length > 0) {
            toast.error(`Allergeni non trovati: ${invalidNums.join(', ')}. Verifica la tabella allergeni nelle impostazioni.`);
            return;
          }
          
          dishesToAdd.push({ name: parsed.name, allergens: codes });
        }
      }
    } else {
      // OLD SYNTAX: comma-separated names, allergens from selector apply to all
      const optionNames = inputText.split(',').map(name => name.trim()).filter(name => name.length > 0);
      dishesToAdd = optionNames.map(name => ({ name, allergens: newOptionAllergens }));
    }
    
    if (dishesToAdd.length === 0) return;
    
    try {
      let addedCount = 0;
      const currentOptionsCount = selectedCategoryForOptions.options?.length || 0;
      
      for (let i = 0; i < dishesToAdd.length; i++) {
        const dish = dishesToAdd[i];
        await api.post(`/categories/${selectedCategoryForOptions.id}/options`, {
          name: dish.name,
          allergens: dish.allergens,
          order: currentOptionsCount + i + 1
        });
        addedCount++;
      }
      
      if (addedCount > 1) {
        toast.success(`${addedCount} opzioni aggiunte`);
      } else {
        toast.success('Opzione aggiunta');
      }
      
      setNewOptionName('');
      setNewOptionAllergens([]);
      fetchCategoriesWithOptions();
      // Update local state
      const updated = await api.get(`/categories/${selectedCategoryForOptions.id}/options`);
      setSelectedCategoryForOptions({
        ...selectedCategoryForOptions,
        options: updated.data
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!selectedCategoryForOptions) return;
    
    try {
      await api.delete(`/categories/${selectedCategoryForOptions.id}/options/${optionId}`);
      toast.success('Opzione eliminata');
      fetchCategoriesWithOptions();
      // Update local state
      setSelectedCategoryForOptions({
        ...selectedCategoryForOptions,
        options: selectedCategoryForOptions.options.filter(o => o.id !== optionId)
      });
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Handle option toggle for quick selection (pranzo/cena)
  const handleOptionToggle = (categoryId, optionId, optionName, allergens) => {
    const key = `${activeDay}-${categoryId}`;
    const currentSelected = selectedDayOptions[key];
    
    if (currentSelected === optionId) {
      // Deselect
      setSelectedDayOptions(prev => {
        const newState = {...prev};
        delete newState[key];
        return newState;
      });
      // Clear the menu item
      handleDishChange(activeDay, categories.find(c => c.id === categoryId)?.code, 'dish_name', '');
    } else {
      // Select
      setSelectedDayOptions(prev => ({...prev, [key]: optionId}));
      // Save to menu
      const category = categoriesWithOptions.find(c => c.id === categoryId);
      if (category) {
        handleDishChange(activeDay, category.code, 'dish_name', optionName);
        handleDishChange(activeDay, category.code, 'allergens', allergens || []);
      }
    }
  };

  // Apply breakfast menu to multiple days
  const handleApplyBreakfastMenu = async () => {
    // This would apply the current day's breakfast config to other days
    // For now, we just show a success message - the actual implementation
    // would need to store breakfast orders differently
    
    if (applyMode === 'week') {
      toast.success('Menù colazione applicato a tutta la settimana');
    } else if (applyUntilDate) {
      toast.success(`Menù colazione applicato fino al ${applyUntilDate.toLocaleDateString('it-IT')}`);
    }
    setApplyMenuDialogOpen(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Print weekly menu
  const handlePrintMenu = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato. Abilita i popup per stampare.');
      return;
    }

    const year = new Date().getFullYear();
    
    // Build HTML for print
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Menu Settimanale - Settimana ${weekNumber}</title>
        <style>
          ${getPrintToolbarCSS()}
          @page { 
            size: A4 landscape; 
            margin: 10mm;
          }
          * { 
            box-sizing: border-box; 
            margin: 0; 
            padding: 0;
          }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 9pt;
            color: #333;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 2px solid #0284c7;
          }
          .header-left {
            text-align: left;
          }
          .structure-name {
            font-size: 12pt;
            font-weight: bold;
            color: #0284c7;
          }
          .header-center {
            text-align: center;
            flex: 1;
          }
          .header h1 {
            font-size: 16pt;
            color: #0284c7;
            margin-bottom: 3px;
          }
          .header p {
            font-size: 10pt;
            color: #666;
          }
          .menu-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
          }
          .menu-table th {
            background: #0284c7;
            color: white;
            padding: 6px 3px;
            font-size: 8pt;
            font-weight: bold;
            text-align: center;
            border: 1px solid #0284c7;
          }
          .menu-table th .date {
            font-weight: normal;
            font-size: 7pt;
            opacity: 0.9;
          }
          .menu-table td {
            padding: 5px 3px;
            border: 1px solid #ddd;
            vertical-align: top;
            font-size: 7pt;
          }
          .menu-table tr:nth-child(even) {
            background: #f8fafc;
          }
          .category-cell {
            background: #f1f5f9 !important;
            font-weight: bold;
            text-align: center;
            width: 70px;
            font-size: 7pt;
          }
          .dish-cell {
            min-height: 18px;
          }
          .allergens {
            color: #dc2626;
            font-size: 6pt;
            font-weight: bold;
          }
          .legend {
            margin-top: 8px;
            padding: 8px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
          }
          .legend h3 {
            font-size: 9pt;
            color: #0284c7;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .legend-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 3px;
            font-size: 6pt;
          }
          .legend-item {
            display: flex;
            gap: 2px;
          }
          .legend-code {
            font-weight: bold;
            color: #dc2626;
          }
          .footer {
            margin-top: 8px;
            text-align: center;
            font-size: 7pt;
            color: #999;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${getPrintToolbarHTML('Menu Settimanale')}
        <div class="print-content">
        <div class="header">
          <div class="header-left">
            <div class="structure-name">${structureName || 'Struttura'}</div>
          </div>
          <div class="header-center">
            <h1>Menu Settimanale</h1>
            <p>Settimana ${weekNumber} - Anno ${year}</p>
            <p style="font-size: 9pt; margin-top: 3px;">
              Dal ${weekDates[0]?.formatted} al ${weekDates[6]?.formatted}
            </p>
          </div>
          <div style="width: 100px;"></div>
        </div>
        
        <table class="menu-table">
          <thead>
            <tr>
              <th>Categoria</th>
              ${DAYS.map((day, i) => `
                <th>
                  ${day.name}<br/>
                  <span class="date">${weekDates[i]?.formatted || ''}</span>
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${categories.map(cat => `
              <tr>
                <td class="category-cell">${cat.name}</td>
                ${DAYS.map(day => {
                  const item = menuItems[day.code]?.[cat.code];
                  const dishName = item?.dish_name || '-';
                  const allergens = item?.allergens?.length > 0 
                    ? `<span class="allergens">(${item.allergens.map(a => a.replace(/[a-z]/gi, '')).join(', ')})</span>` 
                    : '';
                  return `<td class="dish-cell">${dishName} ${allergens}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="legend">
          <h3>⚠️ Legenda Allergeni</h3>
          <div class="legend-grid">
            ${allergens.map(a => `
              <div class="legend-item">
                <span class="legend-code">${a.number}a</span>
                <span>${a.name}</span>
              </div>
            `).join('')}
            ${allergens.map(a => `
              <div class="legend-item">
                <span class="legend-code">${a.number}b</span>
                <span>${a.name} (tracce)</span>
              </div>
            `).join('')}
          </div>
          <p style="margin-top: 8px; font-size: 7pt; color: #666;">
            * I codici "b" indicano possibili tracce dell'allergene
          </p>
        </div>
        
        <div class="footer">
          Stampato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
        </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Print single day menu (elegant version for patients)
  const handlePrintDailyMenu = async (dayIndex) => {
    const day = DAYS[dayIndex];
    const currentYear = new Date().getFullYear();

    // Fetch all meals for the selected day
    try {
      const response = await api.get('/menu', { 
        params: { 
          week_number: weekNumber,
          year: currentYear,
          day: day.code 
        } 
      });
      
      // Group by meal_type
      const menuData = response.data || [];
      const dayMenu = {
        colazione: menuData.filter(item => item.meal_type === 'colazione'),
        pranzo: menuData.filter(item => item.meal_type === 'pranzo'),
        cena: menuData.filter(item => item.meal_type === 'cena')
      };

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Popup bloccato. Abilita i popup per stampare.');
        return;
      }

      // Get structure name - use generic if not available
      const structureDisplayName = activeStructure?.name || structureName || '';

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Menu ${day.name}</title>
          <style>
            ${getPrintToolbarCSS()}
            @page { size: A4 portrait; margin: 20mm; }
            @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Montserrat:wght@300;400;500&display=swap');
            
            * { box-sizing: border-box; margin: 0; padding: 0; }
            
            body { 
              font-family: 'Montserrat', sans-serif; 
              font-size: 11pt; 
              color: #1e3a5f; 
              background: #fff;
              line-height: 1.4;
            }
            
            .menu-container {
              max-width: 100%;
              margin: 0 auto;
              text-align: center;
              padding: 0;
              min-height: calc(100vh - 60px);
              display: flex;
              flex-direction: column;
            }
            
            /* Header */
            .structure-name { 
              font-family: 'Cormorant Garamond', Georgia, serif;
              font-size: 14pt; 
              font-weight: 500;
              color: #1e3a5f;
              letter-spacing: 3px; 
              text-transform: uppercase;
              margin-bottom: 25px;
            }
            
            .menu-title { 
              font-family: 'Cormorant Garamond', Georgia, serif;
              font-size: 42pt; 
              font-weight: 600;
              color: #1e3a5f;
              letter-spacing: 8px;
              margin-bottom: 15px;
            }
            
            .menu-week {
              font-family: 'Montserrat', sans-serif;
              font-size: 10pt;
              color: #5a7a9a;
              font-weight: 400;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            
            .menu-date {
              font-family: 'Montserrat', sans-serif;
              font-size: 12pt;
              color: #1e3a5f;
              font-weight: 500;
              margin-bottom: 40px;
            }
            
            /* Content */
            .menu-content {
              flex: 1;
              text-align: left;
              max-width: 500px;
              margin: 0 auto;
            }
            
            .category-section {
              margin-bottom: 25px;
            }
            
            .category-name {
              font-family: 'Cormorant Garamond', Georgia, serif;
              font-size: 13pt;
              font-weight: 600;
              color: #1e3a5f;
              letter-spacing: 2px;
              text-transform: uppercase;
              margin-bottom: 8px;
              padding-bottom: 5px;
              border-bottom: 1px solid #dde5ed;
            }
            
            .dish-item {
              margin-bottom: 6px;
              padding-left: 5px;
            }
            
            .dish-name {
              font-family: 'Montserrat', sans-serif;
              font-size: 10.5pt;
              font-weight: 400;
              color: #2c4a6b;
            }
            
            .dish-allergens {
              font-size: 9pt;
              color: #c17f24;
              font-weight: 500;
              margin-left: 5px;
            }
            
            .dish-side {
              font-size: 9.5pt;
              font-weight: 300;
              color: #6b8299;
              font-style: italic;
              padding-left: 15px;
              margin-top: 2px;
            }
            
            /* Allergen Legend */
            .allergen-legend {
              margin-top: auto;
              padding-top: 25px;
              border-top: 1px solid #dde5ed;
            }
            
            .legend-title {
              font-family: 'Montserrat', sans-serif;
              font-size: 8pt;
              font-weight: 500;
              color: #5a7a9a;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 8px;
              text-align: left;
            }
            
            .legend-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 4px 15px;
              font-size: 8pt;
              text-align: left;
            }
            
            .legend-item {
              display: flex;
              align-items: center;
              gap: 3px;
            }
            
            .legend-code {
              font-weight: 600;
              color: #c17f24;
              font-family: monospace;
              min-width: 18px;
            }
            
            .legend-name {
              color: #5a7a9a;
              font-weight: 300;
            }
            
            .legend-note {
              font-size: 7pt;
              color: #8fa4b8;
              margin-top: 6px;
              text-align: left;
              font-style: italic;
            }
            
            /* Footer with logo */
            .footer {
              margin-top: 20px;
              padding-top: 15px;
              display: flex;
              justify-content: flex-end;
              align-items: center;
            }
            
            .logo-container {
              text-align: right;
            }
            
            .logo-container img {
              height: 35px;
              object-fit: contain;
            }
            
            @media print { 
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .menu-container { min-height: auto; }
            }
          </style>
        </head>
        <body>
          ${getPrintToolbarHTML('Menu ' + day.name)}
          <div class="print-content">
            <div class="menu-container">
              
              <div class="structure-name">${structureDisplayName}</div>
              <div class="menu-title">MENÙ</div>
              <div class="menu-week">Settimana ${weekNumber}</div>
              <div class="menu-date">${day.name} ${weekDates[DAYS.findIndex(d => d.code === day.code)]?.formatted || ''}</div>
              
              <div class="menu-content">
              ${(() => {
                // Combine pranzo and cena dishes, grouped by category
                const allDishes = {};
                const categoryOrder = [];
                
                ['pranzo', 'cena'].forEach(mealType => {
                  const mealDishes = (dayMenu[mealType] || []).filter(d => d.dish_name);
                  mealDishes.forEach(dish => {
                    const catCode = dish.category_code || 'ALTRO';
                    if (!allDishes[catCode]) {
                      allDishes[catCode] = [];
                      categoryOrder.push(catCode);
                    }
                    // Avoid duplicates
                    if (!allDishes[catCode].find(d => d.dish_name === dish.dish_name)) {
                      allDishes[catCode].push(dish);
                    }
                  });
                });
                
                let html = '';
                categoryOrder.forEach(catCode => {
                  const dishes = allDishes[catCode];
                  const category = categories.find(c => c.code === catCode);
                  const categoryName = category?.name || catCode;
                  
                  html += '<div class="category-section">';
                  html += '<div class="category-name">' + categoryName + '</div>';
                  
                  dishes.forEach(dish => {
                    const allergenCodes = dish.allergens && dish.allergens.length > 0 
                      ? dish.allergens.map(a => a.replace(/[a-z]/gi, '')).join(',')
                      : '';
                    
                    html += '<div class="dish-item">';
                    html += '<span class="dish-name">' + dish.dish_name + '</span>';
                    if (allergenCodes) {
                      html += '<span class="dish-allergens">(' + allergenCodes + ')</span>';
                    }
                    if (dish.side_dish) {
                      const sideAllergens = dish.side_dish_allergens && dish.side_dish_allergens.length > 0
                        ? ' (' + dish.side_dish_allergens.map(a => a.replace(/[a-z]/gi, '')).join(',') + ')'
                        : '';
                      html += '<div class="dish-side">con ' + dish.side_dish + sideAllergens + '</div>';
                    }
                    html += '</div>';
                  });
                  
                  html += '</div>';
                });
                
                return html || '<p style="text-align:center;color:#888;">Nessun piatto configurato per questo giorno</p>';
              })()}
              </div>
              
              <!-- Allergen Legend -->
              <div class="allergen-legend">
                <div class="legend-title">Allergeni</div>
                <div class="legend-grid">
                  ${allergens.map(a => `
                    <div class="legend-item">
                      <span class="legend-code">${a.number}</span>
                      <span class="legend-name">${a.name}</span>
                    </div>
                  `).join('')}
                </div>
                <div class="legend-note">* I codici seguiti da lettera indicano possibili tracce</div>
              </div>
              
              <!-- Footer with Logo -->
              <div class="footer">
                <div class="logo-container">
                  <img src="/logo-horizontal-trimmed.png" alt="ServiCare" />
                </div>
              </div>
              
            </div>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.error('Error fetching menu for print:', error);
      toast.error('Errore nel caricamento del menu');
    }
  };

  // Print multi-day menu (select range)
  const [printRangeDialogOpen, setPrintRangeDialogOpen] = useState(false);
  const [printRangeStart, setPrintRangeStart] = useState(0);
  const [printRangeEnd, setPrintRangeEnd] = useState(6);

  const handlePrintMultiDay = async () => {
    const currentYear = new Date().getFullYear();
    const selectedDays = DAYS.slice(printRangeStart, printRangeEnd + 1);

    try {
      // Fetch menu data for all selected days
      const menuByDay = {};
      for (let idx = 0; idx < selectedDays.length; idx++) {
        const day = selectedDays[idx];
        const response = await api.get('/menu', { 
          params: { 
            week_number: weekNumber,
            year: currentYear,
            day: day.code 
          } 
        });
        const menuData = response.data || [];
        menuByDay[idx] = {
          colazione: menuData.filter(item => item.meal_type === 'colazione'),
          pranzo: menuData.filter(item => item.meal_type === 'pranzo'),
          cena: menuData.filter(item => item.meal_type === 'cena')
        };
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Popup bloccato. Abilita i popup per stampare.');
        return;
      }

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Menu ${selectedDays[0].name} - ${selectedDays[selectedDays.length - 1].name}</title>
          <style>
            ${getPrintToolbarCSS()}
            @page { size: A4 landscape; margin: 10mm; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 8pt; color: #333; }
            .header { text-align: center; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 2px solid #0284c7; }
            .structure-name { font-size: 11pt; font-weight: bold; color: #0284c7; }
            .header h1 { font-size: 14pt; color: #333; margin: 5px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; vertical-align: top; }
            th { background: #0284c7; color: white; font-size: 8pt; text-align: center; }
            th.meal-header { background: #f59e0b; }
            th.meal-header.pranzo { background: #f97316; }
            th.meal-header.cena { background: #6366f1; }
            .day-cell { font-weight: bold; background: #f1f5f9; text-align: center; }
            .dish { font-size: 7pt; margin-bottom: 2px; }
            .dish-name { font-weight: 500; }
            .dish-side { color: #666; font-style: italic; }
            .footer { text-align: center; margin-top: 10px; font-size: 7pt; color: #999; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${getPrintToolbarHTML('Menu ' + selectedDays[0].name + ' - ' + selectedDays[selectedDays.length - 1].name)}
          <div class="print-content">
          <div class="header">
            <div class="structure-name">${activeStructure?.name || structureName || 'Struttura'}</div>
            <h1>Menu ${selectedDays[0].name} - ${selectedDays[selectedDays.length - 1].name}</h1>
            <div>Settimana ${weekNumber}, ${year}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 80px;">Giorno</th>
                <th class="meal-header" style="width: 20%;">☀️ Colazione</th>
                <th class="meal-header pranzo" style="width: 35%;">🌞 Pranzo</th>
                <th class="meal-header cena" style="width: 35%;">🌙 Cena</th>
              </tr>
            </thead>
            <tbody>
              ${selectedDays.map((day, idx) => {
                const dayMenu = menuByDay[idx] || {};
                return `
                  <tr>
                    <td class="day-cell">${day.name}</td>
                    ${['colazione', 'pranzo', 'cena'].map(mealType => {
                      const dishes = (dayMenu[mealType] || []).filter(d => d.dish_name);
                      return `<td>${dishes.map(d => `
                        <div class="dish">
                          <span class="dish-name">${d.dish_name}</span>
                          ${d.side_dish ? `<span class="dish-side"> (${d.side_dish})</span>` : ''}
                        </div>
                      `).join('') || '-'}</td>`;
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="footer">Stampato il ${new Date().toLocaleDateString('it-IT')} - ${activeStructure?.name || structureName || ''}</div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      setPrintRangeDialogOpen(false);
    } catch (error) {
      console.error('Errore nel caricamento del menu per la stampa:', error);
      toast.error('Errore nel caricamento del menu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="menu-config-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5 sm:w-7 sm:h-7 text-blue-700 shrink-0" />
              <span className="truncate">Menu Settimanale</span>
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-1 hidden sm:block">Configura i piatti per ogni giorno</p>
          </div>
          
          {/* Print Options Dropdown - moved to header right */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                data-testid="print-menu-btn"
                className="h-8 text-xs sm:text-sm shrink-0 self-start sm:self-auto"
              >
                <Printer className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Stampa</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Opzioni di Stampa</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handlePrintMenu}>
                <CalendarDays className="w-4 h-4 mr-2" />
                Menu Settimanale
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPrintRangeDialogOpen(true)}>
                <CalendarDays className="w-4 h-4 mr-2" />
                Menu Multigiornaliero...
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-500">Stampa Giornaliera (per Paziente)</DropdownMenuLabel>
              {DAYS.map((day, idx) => (
                <DropdownMenuItem key={day.code} onClick={() => handlePrintDailyMenu(idx)}>
                  {day.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Controls Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Save Status Indicator */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            saveStatus === 'saved' ? 'bg-green-100 text-green-700' :
            saveStatus === 'saving' ? 'bg-blue-100 text-blue-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {saveStatus === 'saved' && <><Check className="w-3 h-3" /> <span>Salvato</span></>}
            {saveStatus === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /> <span>Salvataggio...</span></>}
            {saveStatus === 'pending' && <><Loader2 className="w-3 h-3" /> <span>In attesa...</span></>}
          </div>

          {/* Week Selector */}
          <Select value={weekNumber.toString()} onValueChange={(v) => setWeekNumber(parseInt(v))}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <span>Sett. {weekNumber}</span>
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 52 }, (_, i) => i + 1).map(w => (
                <SelectItem key={w} value={w.toString()}>Settimana {w}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Meal Type Selector */}
          <div className="flex items-center gap-1 ml-auto">
            {MEALS.map(meal => {
              const Icon = meal.icon;
              const isActive = activeMeal === meal.code;
              return (
                <Button
                  key={meal.code}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveMeal(meal.code)}
                  className={`h-8 px-2 sm:px-3 ${isActive ? 
                    meal.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600' :
                    meal.color === 'orange' ? 'bg-orange-500 hover:bg-orange-600' :
                    'bg-indigo-500 hover:bg-indigo-600'
                  : ''}`}
                  data-testid={`meal-${meal.code}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">{meal.name}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Print Range Dialog */}
      <Dialog open={printRangeDialogOpen} onOpenChange={setPrintRangeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Stampa Menu Multigiornaliero</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Da</Label>
                <Select value={printRangeStart.toString()} onValueChange={(v) => setPrintRangeStart(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day, idx) => (
                      <SelectItem key={day.code} value={idx.toString()}>{day.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>A</Label>
                <Select value={printRangeEnd.toString()} onValueChange={(v) => setPrintRangeEnd(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((day, idx) => (
                      <SelectItem key={day.code} value={idx.toString()} disabled={idx < printRangeStart}>{day.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPrintRangeDialogOpen(false)}>Annulla</Button>
              <Button onClick={handlePrintMultiDay}>
                <Printer className="w-4 h-4 mr-2" />
                Stampa
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Button + Dialog */}
      {canManageCategories && (
        <div className="flex justify-end mb-2">
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs sm:text-sm">
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Gestione Categorie</span>
                <span className="sm:hidden">Categorie</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Gestione Categorie Menu</DialogTitle>
              </DialogHeader>
                
                <div className="space-y-6">
                  {/* Add new category */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="text-sm font-medium">Aggiungi nuova categoria</Label>
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Nome categoria"
                        className="flex-1 min-w-[150px]"
                      />
                      <Select value={newCategoryMealType} onValueChange={(v) => {
                        setNewCategoryMealType(v);
                        // Reset has_side_dish if colazione
                        if (v === 'colazione') setNewCategoryHasSideDish(false);
                      }}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="colazione">
                            <span className="flex items-center gap-2">
                              <Coffee className="w-4 h-4 text-amber-600" />
                              Colazione
                            </span>
                          </SelectItem>
                          <SelectItem value="pranzo_cena">
                            <span className="flex items-center gap-2">
                              <Sun className="w-4 h-4 text-orange-600" />
                              Pranzo/Cena
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddCategory} className="bg-blue-700 hover:bg-blue-800">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {newCategoryMealType === 'pranzo_cena' && (
                      <div className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          id="has_side_dish"
                          checked={newCategoryHasSideDish}
                          onChange={(e) => setNewCategoryHasSideDish(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Label htmlFor="has_side_dish" className="text-sm text-slate-600 cursor-pointer">
                          Ha campo contorno (come Carne, Pesce, Vegetariano)
                        </Label>
                      </div>
                    )}
                  </div>
                  
                  {/* Colazione categories */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Coffee className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">Categorie Colazione</h3>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        {allCategories.filter(c => c.meal_type === 'colazione').length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {allCategories.filter(c => c.meal_type === 'colazione').length === 0 ? (
                        <p className="text-sm text-slate-500 italic p-3 bg-amber-50 rounded-lg">
                          Nessuna categoria colazione. Aggiungi categorie come "Bevanda Calda", "Bevanda Fredda", "Dolce"...
                        </p>
                      ) : (
                        allCategories.filter(c => c.meal_type === 'colazione').map((category, index) => (
                          <div 
                            key={category.id}
                            className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
                                {index + 1}
                              </span>
                              <div>
                                <span className="font-medium text-slate-900">{category.name}</span>
                                <span className="text-xs text-slate-500 ml-2 font-mono">({category.code})</span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteCategory(category)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Pranzo/Cena categories */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sun className="w-5 h-5 text-orange-600" />
                      <h3 className="font-semibold text-slate-900">Categorie Pranzo/Cena</h3>
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        {allCategories.filter(c => c.meal_type === 'pranzo_cena' || !c.meal_type).length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {allCategories.filter(c => c.meal_type === 'pranzo_cena' || !c.meal_type).map((category, index) => (
                        <div 
                          key={category.id}
                          className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <div>
                              <span className="font-medium text-slate-900">{category.name}</span>
                              <span className="text-xs text-slate-500 ml-2 font-mono">({category.code})</span>
                              {category.has_side_dish && (
                                <Badge variant="outline" className="ml-2 text-[10px] bg-green-50 border-green-300 text-green-700 py-0">
                                  +contorno
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCategory(category)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
                    <strong>Colazione:</strong> Ogni categoria rappresenta una scelta (es: il paziente sceglie 1 bevanda calda + 1 bevanda fredda + 1 dolce)<br/>
                    <strong>Pranzo/Cena:</strong> Le categorie sono i tipi di piatto (Antipasto, Primo, Secondo, Contorno, Dolce...)
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

      {/* Days Tabs */}
      <Tabs value={activeDay} onValueChange={setActiveDay} className="w-full">
        <TabsList className="grid w-full grid-cols-7 h-auto p-1 bg-slate-100 rounded-lg overflow-x-auto">
          {DAYS.map((day, index) => (
            <TabsTrigger
              key={day.code}
              value={day.code}
              className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm py-1 sm:py-2 px-0.5 sm:px-2 text-[10px] sm:text-sm flex flex-col gap-0 sm:gap-0.5 min-w-0"
            >
              <span className="hidden lg:inline">{day.name}</span>
              <span className="lg:hidden font-medium truncate">{day.name.slice(0, 3)}</span>
              <span className="text-[8px] sm:text-xs text-slate-400 font-normal">{weekDates[index]?.formatted}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {DAYS.map((day, index) => (
          <TabsContent key={day.code} value={day.code} className="mt-4 sm:mt-6">
            <Card className="border-slate-200">
              <CardHeader className="bg-blue-50 border-b border-slate-200 py-3 sm:py-4">
                <CardTitle className="text-base sm:text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
                  {day.name} {weekDates[index]?.formatted}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {categories.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>Nessuna categoria configurata</p>
                    {canManageCategories && (
                      <Button 
                        variant="outline" 
                        className="mt-2"
                        onClick={() => setCategoryDialogOpen(true)}
                      >
                        Aggiungi categorie
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {activeMeal === 'colazione' ? (
                      // COLAZIONE: Show options as badges (read-only view) + Apply button
                      <div className="flex flex-col gap-3">
                        {categoriesWithOptions.map(category => {
                          const hasOptions = category.options && category.options.length > 0;
                          
                          return (
                            <div key={category.id} className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <Label className="text-sm font-medium text-amber-800 uppercase tracking-wide">
                                  {category.name}
                                </Label>
                                {canEditMenu && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openOptionsDialog(category)}
                                    className="h-7 text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                                  >
                                    <Settings className="w-3 h-3" />
                                    {hasOptions ? `${category.options.length} opzioni` : 'Aggiungi'}
                                  </Button>
                                )}
                              </div>
                              
                              {hasOptions ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {category.options.map(option => (
                                    <Badge 
                                      key={option.id} 
                                      variant="outline" 
                                      className="text-xs bg-white border-amber-200 text-amber-800 py-1 px-2"
                                    >
                                      {option.name}
                                      {option.allergens?.length > 0 && (
                                        <span className="ml-1 text-amber-600 font-mono text-[10px]">
                                          ({getAllergenNumbers(option.allergens)})
                                        </span>
                                      )}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-amber-600/70 italic">
                                  Clicca "Aggiungi" per configurare le opzioni
                                </p>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Apply Breakfast Menu Button */}
                        {canEditMenu && categoriesWithOptions.some(c => c.options?.length > 0) && (
                          <div className="pt-4 border-t border-amber-200 mt-2">
                            <Button
                              onClick={() => setApplyMenuDialogOpen(true)}
                              className="w-full bg-amber-500 hover:bg-amber-600 text-white gap-2"
                            >
                              <Copy className="w-4 h-4" />
                              Applica Menù Colazione...
                            </Button>
                            <p className="text-xs text-amber-600 text-center mt-2">
                              Applica questa configurazione colazione a più giorni
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      // PRANZO/CENA: Grid layout with dropdown for preset options
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                        {/* Filter out carboidrato and verdura - they appear as contorni options */}
                        {categoriesWithOptions
                          .filter(c => c.code !== 'carboidrato' && c.code !== 'verdura')
                          .map(category => {
                          const hasOptions = category.options && category.options.length > 0;
                          const currentDish = menuItems[day.code]?.[category.code]?.dish_name || '';
                          const currentAllergens = menuItems[day.code]?.[category.code]?.allergens || [];
                          
                          return (
                            <div key={category.id} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                                  {category.name}
                                </Label>
                                {canEditMenu && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openOptionsDialog(category)}
                                    className="h-7 text-xs gap-1"
                                  >
                                    <Settings className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                              
                              {/* Input with autocomplete and preset dropdown */}
                              <div className="flex gap-2">
                                <div className="flex-1 relative">
                                  <AutocompleteInput
                                    value={currentDish}
                                    onChange={(val) => handleDishChange(day.code, category.code, 'dish_name', val)}
                                    onSelect={(option) => {
                                      handleDishChange(day.code, category.code, 'dish_name', option.name);
                                      handleDishChange(day.code, category.code, 'allergens', option.allergens || []);
                                    }}
                                    options={category.options || []}
                                    placeholder={`${category.name}...`}
                                    disabled={!canEditMenu}
                                    className={`h-10 pr-10 ${!canEditMenu ? 'bg-slate-100' : ''}`}
                                    getAllergenNumbers={getAllergenNumbers}
                                  />
                                  {canEditMenu && hasOptions && (
                                    <div className="absolute right-1 top-1 z-10">
                                      <DishSelector
                                        options={category.options}
                                        currentValue={currentDish}
                                        onSelect={(option) => {
                                          handleDishChange(day.code, category.code, 'dish_name', option.name);
                                          handleDishChange(day.code, category.code, 'allergens', option.allergens || []);
                                        }}
                                        placeholder="Cerca piatto..."
                                        title="Scegli piatto"
                                        getAllergenNumbers={getAllergenNumbers}
                                        showLastUsed={true}
                                        triggerClassName="h-8 w-8"
                                      />
                                    </div>
                                  )}
                                </div>
                                {canEditMenu && (
                                  <AllergenSelector
                                    selected={currentAllergens}
                                    onChange={(allergens) => handleDishChange(day.code, category.code, 'allergens', allergens)}
                                    allergensList={allergens}
                                  />
                                )}
                              </div>
                              
                              {/* Show allergens if any - clickable to remove */}
                              {currentAllergens.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {currentAllergens.map(code => {
                                    const isTrace = code.endsWith('b');
                                    const num = code.replace(/[a-z]/gi, '');
                                    return (
                                      <Badge 
                                        key={code} 
                                        variant="outline" 
                                        className={`text-xs py-0.5 pr-1 ${isTrace ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-amber-50 border-amber-300 text-amber-700'} ${canEditMenu ? 'cursor-pointer hover:opacity-70' : ''}`}
                                        onClick={() => {
                                          if (canEditMenu) {
                                            const newAllergens = currentAllergens.filter(a => a !== code);
                                            handleDishChange(day.code, category.code, 'allergens', newAllergens);
                                          }
                                        }}
                                        title={isTrace ? `Allergene ${num} (tracce) - clicca per rimuovere` : `Allergene ${num} - clicca per rimuovere`}
                                      >
                                        {num}{isTrace && <span className="text-[9px] ml-0.5">t</span>}
                                        {canEditMenu && (
                                          <X className="w-3 h-3 ml-1 hover:text-red-600" />
                                        )}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Side dish field for categories with has_side_dish */}
                              {category.has_side_dish && (() => {
                                // Find carboidrato and verdura category options for contorni
                                const carboidratoCategory = categoriesWithOptions.find(c => c.code === 'carboidrato');
                                const verduraCategory = categoriesWithOptions.find(c => c.code === 'verdura');
                                const carboidratoOptions = carboidratoCategory?.options || [];
                                const verduraOptions = verduraCategory?.options || [];
                                const allContorniOptions = [...carboidratoOptions, ...verduraOptions];
                                const currentSideDish = menuItems[day.code]?.[category.code]?.side_dish || '';
                                
                                // Group options for DishSelector
                                const groupedOptions = {};
                                if (carboidratoOptions.length > 0) groupedOptions['Carboidrato'] = carboidratoOptions;
                                if (verduraOptions.length > 0) groupedOptions['Verdura'] = verduraOptions;
                                
                                return (
                                  <div className="mt-2 pt-2 border-t border-slate-200">
                                    <Label className="text-xs text-slate-500 mb-1 block">Contorno</Label>
                                    <div className="flex gap-2">
                                      <div className="flex-1 relative">
                                        <AutocompleteInput
                                          value={currentSideDish}
                                          onChange={(val) => handleDishChange(day.code, category.code, 'side_dish', val)}
                                          onSelect={(option) => {
                                            handleDishChange(day.code, category.code, 'side_dish', option.name);
                                            handleDishChange(day.code, category.code, 'side_dish_allergens', option.allergens || []);
                                          }}
                                          placeholder="es: patate arrosto, insalata mista..."
                                          disabled={!canEditMenu}
                                          className={`h-8 text-sm pr-10 ${!canEditMenu ? 'bg-slate-100' : ''}`}
                                          getAllergenNumbers={getAllergenNumbers}
                                          groupedOptions={Object.keys(groupedOptions).length > 0 ? groupedOptions : null}
                                          options={allContorniOptions}
                                        />
                                        {canEditMenu && allContorniOptions.length > 0 && (
                                          <div className="absolute right-1 top-0.5 z-10">
                                            <DishSelector
                                              currentValue={currentSideDish}
                                              onSelect={(option) => {
                                                handleDishChange(day.code, category.code, 'side_dish', option.name);
                                                handleDishChange(day.code, category.code, 'side_dish_allergens', option.allergens || []);
                                              }}
                                              placeholder="Cerca contorno..."
                                              title="Scegli contorno"
                                              getAllergenNumbers={getAllergenNumbers}
                                              showLastUsed={false}
                                              triggerClassName="h-7 w-7 hover:bg-green-100"
                                              groupedOptions={groupedOptions}
                                            />
                                          </div>
                                        )}
                                      </div>
                                      {canEditMenu && (
                                        <AllergenSelector
                                          selected={menuItems[day.code]?.[category.code]?.side_dish_allergens || []}
                                          onChange={(allergens) => handleDishChange(day.code, category.code, 'side_dish_allergens', allergens)}
                                          allergensList={allergens}
                                        />
                                      )}
                                    </div>
                                    {/* Side dish allergens */}
                                    {(menuItems[day.code]?.[category.code]?.side_dish_allergens || []).length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {menuItems[day.code][category.code].side_dish_allergens.map(code => {
                                          const isTrace = code.endsWith('b');
                                          const num = code.replace(/[a-z]/gi, '');
                                          return (
                                            <Badge 
                                              key={code} 
                                              variant="outline" 
                                              className={`text-[10px] py-0 pr-1 ${isTrace ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-green-50 border-green-300 text-green-700'} ${canEditMenu ? 'cursor-pointer hover:opacity-70' : ''}`}
                                              onClick={() => {
                                                if (canEditMenu) {
                                                  const newAllergens = menuItems[day.code][category.code].side_dish_allergens.filter(a => a !== code);
                                                  handleDishChange(day.code, category.code, 'side_dish_allergens', newAllergens);
                                                }
                                              }}
                                              title={isTrace ? `Allergene ${num} (tracce)` : `Allergene ${num}`}
                                            >
                                              {num}{isTrace && <span className="text-[8px] ml-0.5">t</span>}
                                              {canEditMenu && <X className="w-2 h-2 ml-0.5 hover:text-red-600" />}
                                            </Badge>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {categoriesWithOptions.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <p>Nessuna categoria configurata per questo pasto.</p>
                        <p className="text-sm mt-1">Vai su "Categorie" per aggiungerne.</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Allergen Legend */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Legenda Allergeni
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-4 gap-y-1 text-xs">
            {allergens.map(allergen => (
              <div key={allergen.code} className="flex items-center gap-1 py-0.5">
                <span className="font-mono font-bold text-amber-600 shrink-0">{allergen.number}a</span>
                <span className="text-slate-600 truncate">{allergen.name}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            * I codici "b" indicano possibili tracce
          </p>
        </CardContent>
      </Card>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Elimina Categoria
            </DialogTitle>
          </DialogHeader>
          {categoryToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-slate-700">
                  Stai per eliminare la categoria:
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">{categoryToDelete.name}</p>
                <p className="text-sm text-slate-500 mt-1 font-mono">({categoryToDelete.code})</p>
                <Badge 
                  variant="outline" 
                  className={`mt-2 ${categoryToDelete.meal_type === 'colazione' ? 'text-amber-600 border-amber-300' : 'text-orange-600 border-orange-300'}`}
                >
                  {categoryToDelete.meal_type === 'colazione' ? 'Colazione' : 'Pranzo/Cena'}
                </Badge>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <strong>Attenzione:</strong> Eliminando questa categoria, i piatti associati non saranno più visibili nel menu.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDeleteCategoryDialogOpen(false);
                    setCategoryToDelete(null);
                  }}
                  disabled={deleteCategoryLoading}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={confirmDeleteCategory}
                  disabled={deleteCategoryLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleteCategoryLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Conferma Eliminazione
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Options Management Dialog */}
      <Dialog open={optionDialogOpen} onOpenChange={setOptionDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Opzioni: {selectedCategoryForOptions?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCategoryForOptions && (
            <div className="space-y-4">
              {/* Add new option */}
              {canEditMenu && (
                <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                  <Label className="text-sm font-medium">Aggiungi nuova opzione</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOptionName}
                      onChange={(e) => setNewOptionName(e.target.value)}
                      placeholder="es: Pasta 1,7; Risotto 1; Insalata"
                      className="flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
                    />
                    <AllergenSelector
                      selected={newOptionAllergens}
                      onChange={setNewOptionAllergens}
                      allergensList={allergens}
                    />
                    <Button onClick={handleAddOption} className="bg-blue-700 hover:bg-blue-800">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    💡 <strong>Nuova sintassi:</strong> Separa i piatti con <code className="bg-slate-200 px-1 rounded">;</code> e aggiungi i numeri allergeni dopo il nome.
                    <br />Esempio: <code className="bg-slate-200 px-1 rounded">Pasta al pomodoro 1,7; Risotto 1; Insalata</code>
                    <br /><span className="text-slate-400">Oppure usa la vecchia sintassi (virgola) con il selettore allergeni.</span>
                  </p>
                </div>
              )}
              
              {/* Options list */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Opzioni disponibili ({selectedCategoryForOptions.options?.length || 0})
                </Label>
                
                {(!selectedCategoryForOptions.options || selectedCategoryForOptions.options.length === 0) ? (
                  <p className="text-sm text-slate-500 italic p-4 bg-slate-50 rounded-lg text-center">
                    Nessuna opzione configurata. Aggiungi le opzioni sopra.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedCategoryForOptions.options.map((option, index) => {
                      // Only show last_used for pranzo/cena categories, not for colazione
                      const isColazione = selectedCategoryForOptions.meal_type === 'colazione';
                      let lastUsedText = null;
                      let lastUsedClass = '';
                      
                      if (!isColazione && option.last_used) {
                        const lastUsed = new Date(option.last_used);
                        const today = new Date();
                        const diffDays = Math.floor((today - lastUsed) / (1000 * 60 * 60 * 24));
                        
                        // Inverted colors: longer ago = green (good), recent = red (avoid)
                        if (diffDays === 0) {
                          lastUsedText = 'usato oggi';
                          lastUsedClass = 'text-red-500 font-medium';
                        } else if (diffDays === 1) {
                          lastUsedText = 'usato ieri';
                          lastUsedClass = 'text-red-500';
                        } else if (diffDays <= 3) {
                          lastUsedText = `${diffDays}gg fa`;
                          lastUsedClass = 'text-amber-500';
                        } else if (diffDays <= 7) {
                          lastUsedText = `${diffDays}gg fa`;
                          lastUsedClass = 'text-blue-500';
                        } else {
                          lastUsedText = `${diffDays}gg fa ✓`;
                          lastUsedClass = 'text-green-600 font-medium';
                        }
                      } else if (!isColazione) {
                        lastUsedText = 'mai usato ✓';
                        lastUsedClass = 'text-green-600 font-medium';
                      }
                      
                      return (
                      <div 
                        key={option.id}
                        className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </span>
                          <div>
                            <span className="font-medium text-slate-900">{option.name}</span>
                            <div className="flex items-center gap-2 mt-1">
                              {option.allergens?.length > 0 && (
                                <div className="flex gap-1">
                                  {option.allergens.map(code => (
                                    <Badge key={code} variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-700 py-0 px-1">
                                      {code}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {lastUsedText && (
                                <span className={`text-[10px] italic ${lastUsedClass}`}>
                                  ⏱ {lastUsedText}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {canEditMenu && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteOption(option.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <p className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
                <strong>Suggerimento:</strong> Aggiungi tutte le opzioni che vuoi offrire per questa categoria. 
                Durante le comande, potrai selezionare quale opzione ha scelto ogni paziente.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Breakfast Menu Dialog */}
      <Dialog open={applyMenuDialogOpen} onOpenChange={setApplyMenuDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Copy className="w-5 h-5" />
              Applica Menù Colazione
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Le opzioni colazione configurate saranno valide per tutti i giorni selezionati.
            </p>
            
            <div className="space-y-3">
              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  applyMode === 'week' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-slate-200 hover:border-amber-300'
                }`}
                onClick={() => setApplyMode('week')}
              >
                <div className="flex items-center gap-3">
                  <CalendarDays className={`w-5 h-5 ${applyMode === 'week' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <div>
                    <p className="font-medium text-slate-900">Tutta la settimana</p>
                    <p className="text-xs text-slate-500">Applica a tutti i 7 giorni della settimana {weekNumber}</p>
                  </div>
                </div>
              </div>
              
              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  applyMode === 'until' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-slate-200 hover:border-amber-300'
                }`}
                onClick={() => setApplyMode('until')}
              >
                <div className="flex items-center gap-3">
                  <Calendar className={`w-5 h-5 ${applyMode === 'until' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Fino a data specifica</p>
                    <p className="text-xs text-slate-500">Applica da oggi fino alla data selezionata</p>
                    {applyMode === 'until' && (
                      <Input
                        type="date"
                        className="mt-2 h-9"
                        value={applyUntilDate ? applyUntilDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => setApplyUntilDate(e.target.value ? new Date(e.target.value) : null)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setApplyMenuDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                onClick={handleApplyBreakfastMenu}
                disabled={applyMode === 'until' && !applyUntilDate}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Applica
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
